import { AsyncLocalStorage } from 'async_hooks';
import { Injectable } from '@nestjs/common';
import { randomBytes, randomUUID } from 'crypto';

/**
 * The actor payload handed to Postgres as the transaction-local `audit.actor`
 * setting, which `audit.fn_log_change()` reads back. Keep the keys in sync with
 * that function (see prisma/migrations/manual/045_audit_batching_and_triggers.sql).
 */
export interface AuditActor {
  batchId: string;
  correlationId?: string;
  actorId?: string;
  actorRole?: string;
  actorName?: string;
  ipAddress?: string;
  storeId?: string;
  organizationId?: string;
  source?: string;
}

export interface AuditContextState extends AuditActor {
  userAgent?: string;
  route?: string;
  method?: string;
  /** Dotted intent set by the service layer, e.g. "product.update". */
  action?: string;
  /** Human-readable label for the collapsed admin row. */
  summary?: string;
  /** Set once a batch header row has been written, so it is written once. */
  headerWritten?: boolean;
}

let lastTimestampMs = 0;
let sequence = 0;

/**
 * Time-ordered UUID (RFC 9562 v7).
 *
 * Deliberately not a raw millisecond timestamp: two merchants saving in the
 * same millisecond would collide, and the admin view would then present two
 * unrelated people's edits as one batch. v7 keeps the property that was
 * actually wanted — it sorts chronologically, like a timestamp — while staying
 * unique, and unlike `txId` it survives an archive-and-reimport.
 */
export function uuidv7(): string {
  const ms = Date.now();
  const bytes = randomBytes(16);

  // Monotonic counter in rand_a (RFC 9562 method 1). Without it, two ids minted
  // in the SAME millisecond differ only in random bits, so their sort order is
  // a coin flip — and "sorts chronologically" is the entire reason this is used
  // as the batch key instead of a plain timestamp.
  if (ms === lastTimestampMs) {
    sequence = (sequence + 1) & 0x0fff;
  } else {
    lastTimestampMs = ms;
    sequence = 0;
  }

  bytes[0] = (ms / 0x10000000000) & 0xff;
  bytes[1] = (ms / 0x100000000) & 0xff;
  bytes[2] = (ms / 0x1000000) & 0xff;
  bytes[3] = (ms / 0x10000) & 0xff;
  bytes[4] = (ms / 0x100) & 0xff;
  bytes[5] = ms & 0xff;

  bytes[6] = 0x70 | ((sequence >> 8) & 0x0f); // version 7 + counter high nibble
  bytes[7] = sequence & 0xff;                 // counter low byte
  bytes[8] = 0x80 | (bytes[8] & 0x3f);        // variant 10

  const hex = bytes.toString('hex');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

/**
 * Request-scoped audit identity.
 *
 * Mirrors StoreContextService's AsyncLocalStorage pattern so the two behave the
 * same way at the edges of a request.
 */
@Injectable()
export class AuditContextService {
  private readonly storage = new AsyncLocalStorage<AuditContextState>();

  run<T>(state: AuditContextState, callback: () => T): T {
    return this.storage.run(state, callback);
  }

  current(): AuditContextState | undefined {
    return this.storage.getStore();
  }

  batchId(): string | undefined {
    return this.storage.getStore()?.batchId;
  }

  correlationId(): string | undefined {
    return this.storage.getStore()?.correlationId;
  }

  /**
   * Labels the current batch. Called from services at the point where the
   * intent is known — this is what turns "9 rows changed" into "Updated product
   * Blue Kurta" in the collapsed admin row.
   *
   * First label wins: an outer service method describes the user's action
   * better than the inner repository call it delegates to.
   */
  describe(action: string, summary?: string): void {
    const state = this.storage.getStore();
    if (!state || state.action) return;

    state.action = action;
    if (summary) state.summary = summary;
  }

  markHeaderWritten(): void {
    const state = this.storage.getStore();
    if (state) state.headerWritten = true;
  }

  /**
   * JSON for `set_config('audit.actor', ..., true)`. Returns undefined when
   * there is no request context — a cron or seed write, which the trigger
   * records as `system` rather than guessing an actor.
   */
  actorPayload(): string | undefined {
    const state = this.storage.getStore();
    if (!state) return undefined;

    const actor: AuditActor = {
      batchId: state.batchId,
      correlationId: state.correlationId,
      actorId: state.actorId,
      actorRole: state.actorRole,
      actorName: state.actorName,
      ipAddress: state.ipAddress,
      storeId: state.storeId,
      organizationId: state.organizationId,
      source: state.source,
    };

    return JSON.stringify(
      Object.fromEntries(Object.entries(actor).filter(([, v]) => v !== undefined))
    );
  }

  /** A fresh context for work with no HTTP request behind it (crons, jobs). */
  static systemState(source: string): AuditContextState {
    return {
      batchId: uuidv7(),
      correlationId: randomUUID(),
      actorId: `system:${source}`,
      actorRole: 'system',
      source,
    };
  }
}
